import { API_BASE, ATHENA_PLATFORM, IS_NATIVE_APP } from '../config/api';
import { getAthenaVersionInfo } from '../config/version';
import { resolveAthenaDeepLink } from '../platform/NativeBridge';
import {
  getAthenaPushStatus,
  observeAthenaForeground,
  registerAthenaPush,
  testAthenaPush,
} from '../utils/firebaseMessaging';
import {
  addNativeNotificationsListener,
  cancelNativeAthenaAlarm,
  dismissNativeAthenaAlarm,
  getNativeNotificationPermission,
  getNativeNotificationsCapabilities,
  listNativeAthenaAlarms,
  getNativeNotificationsPlugin,
  getNativePendingNotificationEvents,
  getNativePushRegistration,
  isNativeNotificationsPluginAvailable,
  requestNativeNotificationPermission,
  scheduleNativeAthenaAlarm,
  snoozeNativeAthenaAlarm,
} from './NativeNotificationsPlugin';

/**
 * Platform-neutral notification edge for Athena.
 *
 * Firebase Web Messaging remains the web/PWA delivery mechanism. Android only
 * talks to a future native Capacitor plugin; it never registers a web service
 * worker, asks the browser Notification API, or invokes Firebase Web Messaging
 * from the Capacitor WebView.
 */

export const ATHENA_NOTIFICATION_EVENTS = Object.freeze({
  RECEIVED: 'athena-notification-received',
  ACTION: 'athena-notification-action',
  TOKEN_REFRESH: 'athena-native-push-token-refresh',
  LEGACY_FOREGROUND: 'athena-push-foreground',
});

const ALLOWED_TYPES = new Set([
  'reminder', 'alarm', 'proactive', 'task', 'calendar', 'system', 'follow_up', 'test',
]);
const ALLOWED_PRIORITIES = new Set(['low', 'normal', 'high', 'critical', 'alarm']);
const BLOCKED_ACTIONS = new Set(['dismiss', 'snooze', 'complete', 'cancel', 'delete']);
const ALLOWED_QUERY_KEYS = new Set(['notification_id', 'context_id', 'source']);
const NATIVE_EVENT_ALIASES = Object.freeze({
  notificationforeground: 'notification_foreground',
  notificationaction: 'notification_action',
  notificationopened: 'notification_opened',
  notificationreceived: 'notification_received',
  notificationdisplayed: 'notification_displayed',
  pushtokenrefresh: 'push_token_refresh',
});

const noop = () => {};

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function cleanText(value, maximum) {
  if (typeof value !== 'string') return '';
  return value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, maximum);
}

function cleanIdentifier(value, maximum = 160) {
  const id = cleanText(value, maximum);
  return /^[A-Za-z0-9._:-]+$/.test(id) ? id : '';
}

function cleanType(value) {
  const type = cleanText(value, 48).toLowerCase().replace(/[\s-]+/g, '_');
  if (type === 'event') return 'calendar';
  return ALLOWED_TYPES.has(type) ? type : 'system';
}

function cleanPriority(value) {
  const priority = cleanText(value, 24).toLowerCase();
  return ALLOWED_PRIORITIES.has(priority) ? priority : 'normal';
}

function nativeEventName(payload, fallback = '') {
  const root = asRecord(payload);
  const raw = root.event || root.event_type || root.eventType || root.native_event || fallback || root.type;
  const normalized = cleanText(raw, 80)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s.-]+/g, '_');
  return NATIVE_EVENT_ALIASES[normalized.replace(/_/g, '')] || normalized;
}

function extractPayloadData(payload) {
  const root = asRecord(payload);
  const notification = asRecord(root.notification);
  const eventPayload = asRecord(root.payload || root.detail || root.value);
  const rootData = asRecord(root.data);
  const notificationData = asRecord(notification.data);
  const eventData = asRecord(eventPayload.data);
  // FCM uses `data`, while custom Capacitor events commonly expose details on
  // the root. Data wins deliberately: it is the signed delivery envelope.
  return {
    ...notification,
    ...root,
    ...eventPayload,
    ...notificationData,
    ...eventData,
    ...rootData,
  };
}

/**
 * Only same-app routes and a short set of context parameters are accepted from
 * push payloads. Payloads cannot open browser URLs, execute commands, or
 * smuggle arbitrary query parameters into an authenticated screen.
 */
export function sanitizeAthenaNotificationRoute(value) {
  if (typeof value !== 'string' || !value.trim()) return null;

  let route = value.trim();
  if (/^(?:athena:|https?:)/i.test(route)) {
    route = resolveAthenaDeepLink(route) || '';
  }
  if (!route || !route.startsWith('/')) return null;

  let parsed;
  try {
    parsed = new URL(route, 'https://athena.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'https://athena.invalid') return null;

  const path = parsed.pathname.replace(/\/{2,}/g, '/');
  const routeMatch = path.match(/^\/(?:workspace(?:\/(chats|projects|planning|calendar|tasks|library|tools)(?:\/([A-Za-z0-9._:-]{1,160}))?)?)?$/);
  if (!routeMatch) return null;

  const query = new URLSearchParams();
  parsed.searchParams.forEach((rawValue, key) => {
    if (!ALLOWED_QUERY_KEYS.has(key)) return;
    const safeValue = cleanIdentifier(rawValue, 160);
    if (safeValue) query.set(key, safeValue);
  });
  const search = query.toString();
  return `${path}${search ? `?${search}` : ''}`;
}

/**
 * Converts data from FCM/native into a UI-safe envelope. It intentionally
 * ignores commands, arbitrary metadata, external URLs and unrecognised action
 * names. The backend record remains the source of truth for the message.
 */
export function normalizeAthenaNotificationPayload(payload) {
  const data = extractPayloadData(payload);
  const id = cleanIdentifier(data.notification_id || data.notificationId || data.id);
  const route = sanitizeAthenaNotificationRoute(data.route || data.url || data.deepLink || data.deeplink);
  const title = cleanText(data.title, 120);
  const body = cleanText(data.body, 360);
  const action = cleanText(data.actionId || data.action_id || data.action, 48).toLowerCase();

  if (!id && !route && !title && !body) return null;
  return Object.freeze({
    id: id || null,
    type: cleanType(data.type),
    title: title || 'Athena',
    body,
    route,
    contextId: cleanIdentifier(data.context_id || data.contextId),
    deliveryId: cleanIdentifier(data.delivery_id || data.deliveryId),
    deviceId: cleanIdentifier(data.device_id || data.deviceId),
    nativeEventId: cleanIdentifier(data.event_id || data.eventId),
    snoozeMinutes: Number(data.snooze_minutes || data.snoozeMinutes || data.minutes) || null,
    priority: cleanPriority(data.priority),
    action: action || 'open',
    canOpen: !BLOCKED_ACTIONS.has(action),
  });
}

function emit(name, detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

const CLIENT_NOTIFICATION_EVENTS = new Set(['received', 'displayed', 'opened', 'dismissed', 'snoozed', 'action_clicked']);

/**
 * Records a device-side lifecycle signal without trusting notification text or
 * arbitrary payload commands. It is best-effort: the visible experience never
 * waits for analytics/delivery reconciliation.
 */
export async function reportAthenaNotificationEvent(notification, event, authToken = null) {
  if (!notification?.id || !CLIENT_NOTIFICATION_EVENTS.has(event)) return false;
  const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('athena_token') : null);
  if (!token) return false;

  const body = {
    event,
    // Stable per notification/delivery/event prevents repeated WebView
    // listener attachment from creating duplicate delivery state writes.
    event_id: `${notification.id}:${notification.deliveryId || notification.deviceId || 'client'}:${event}`.slice(0, 120),
  };
  if (notification.deliveryId) body.delivery_id = notification.deliveryId;
  if (notification.deviceId) body.device_id = notification.deviceId;
  if (event === 'snoozed' && (notification.snoozeMinutes === 5 || notification.snoozeMinutes === 10)) {
    body.snooze_minutes = notification.snoozeMinutes;
  }

  try {
    const response = await fetch(`${API_BASE}/notifications/${encodeURIComponent(notification.id)}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function emitAthenaNotificationReceived(payload) {
  const notification = normalizeAthenaNotificationPayload(payload);
  if (!notification) return null;
  emit(ATHENA_NOTIFICATION_EVENTS.RECEIVED, notification);
  // Existing UI code already listens for this event to reconcile the bell.
  emit(ATHENA_NOTIFICATION_EVENTS.LEGACY_FOREGROUND, notification);
  // A foreground payload is both received and shown by the in-app central.
  // This is not a provider acknowledgement: it is the device-side lifecycle
  // signal that lets the Worker mark the delivery accurately.
  void reportAthenaNotificationEvent(notification, 'received');
  void reportAthenaNotificationEvent(notification, 'displayed');
  return notification;
}

export function emitAthenaNotificationAction(payload) {
  const notification = normalizeAthenaNotificationPayload(payload);
  if (!notification) return null;
  emit(ATHENA_NOTIFICATION_EVENTS.ACTION, notification);
  emit(ATHENA_NOTIFICATION_EVENTS.LEGACY_FOREGROUND, notification);
  if (notification.canOpen) void reportAthenaNotificationEvent(notification, 'opened');
  return notification;
}

export function emitAthenaNativeTokenRefresh(payload) {
  const data = extractPayloadData(payload);
  const token = cleanText(data.token || data.pushToken, 4096);
  if (!token) return null;
  const detail = Object.freeze({ token });
  emit(ATHENA_NOTIFICATION_EVENTS.TOKEN_REFRESH, detail);
  return detail;
}

function browserPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'default';
}

export async function getAthenaNotificationStatus() {
  if (!IS_NATIVE_APP) {
    return {
      platform: 'web',
      supported: typeof Notification !== 'undefined' && typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      permission: browserPermission(),
      nativePlugin: false,
    };
  }

  const [permission, capabilities] = await Promise.all([
    getNativeNotificationPermission(),
    getNativeNotificationsCapabilities(),
  ]);
  return {
    platform: ATHENA_PLATFORM,
    supported: permission.available === true && capabilities.notifications !== false,
    permission: permission.permission,
    nativePlugin: isNativeNotificationsPluginAvailable(),
    pushEnabled: capabilities.pushEnabled === true,
    capabilities,
  };
}

/** Ask only after a user-visible Athena prompt, never during app boot. */
export async function requestAthenaNotificationsPermission() {
  if (!IS_NATIVE_APP) {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return Notification.permission;
    try {
      return await Notification.requestPermission();
    } catch {
      return 'denied';
    }
  }

  const result = await requestNativeNotificationPermission();
  return result.permission;
}

async function registerNativeAthenaPush(authToken, { userInitiated = false } = {}) {
  if (!authToken) return { ok: false, reason: 'session' };
  // FCM auto-init stays off until the person explicitly enables it. Reading a
  // token can initialise Firebase on some devices, so do not call getToken on
  // ordinary app boot. A previously enabled native status may reconcile later.
  const status = await getAthenaNotificationStatus();
  if (!userInitiated && status.pushEnabled !== true) {
    return { ok: false, reason: 'native_activation_required' };
  }
  const registration = await getNativePushRegistration();
  if (!registration?.token) return { ok: false, reason: 'native_push_token_unavailable' };
  if (!registration.deviceId) return { ok: false, reason: 'native_device_identity_unavailable' };

  const version = getAthenaVersionInfo();
  const response = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      token: registration.token,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza',
      device_id: registration.deviceId,
      // These are a deliberate, stable Worker contract for an Android FCM
      // installation. Do not infer the platform from a WebView user agent.
      platform: 'android-native',
      user_agent: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '',
      app_version: registration.appVersion || version.appVersion,
      android_version: registration.androidVersion || version.nativeVersion || '',
      push_enabled: true,
      token_type: 'fcm_android',
    }),
  });
  if (!response.ok) throw new Error(`Não foi possível registrar notificações nativas (${response.status})`);
  return {
    ok: true,
    device_id: registration.deviceId,
    platform: 'android-native',
    token_type: 'fcm_android',
  };
}

/** Register the token in the shared Worker registry through the correct provider. */
export async function registerAthenaNotifications(authToken, options = {}) {
  return IS_NATIVE_APP ? registerNativeAthenaPush(authToken, options) : registerAthenaPush(authToken);
}

/**
 * Mirrors an already-persisted server alarm to Android. Normal reminders are
 * intentionally excluded: their timing remains owned by the Worker/cron and
 * no browser timeout is ever installed as a fallback.
 */
export async function scheduleAthenaAlarmForReminder(reminder, { route = '/' } = {}) {
  if (!IS_NATIVE_APP) return { scheduled: false, reason: 'not_native' };
  if (!reminder || reminder.kind !== 'alarm') return { scheduled: false, reason: 'server_only_reminder' };

  const alarmId = cleanIdentifier(reminder.id, 120);
  const triggerAt = Date.parse(reminder.due_at || reminder.dueAt || '');
  if (!alarmId || !Number.isFinite(triggerAt) || triggerAt <= Date.now()) {
    return { scheduled: false, reason: 'invalid_server_alarm' };
  }

  return scheduleNativeAthenaAlarm({
    // The reminder is the local AlarmManager identity. The Worker-created
    // notification ID is separate and is reused later by the FCM cron, which
    // keeps local display, notification center and delivery events correlated.
    alarmId,
    notificationId: cleanIdentifier(reminder.notification_id || reminder.notificationId, 120) || alarmId,
    triggerAt,
    title: 'Despertador da Athena',
    body: cleanText(reminder.body, 360),
    route: sanitizeAthenaNotificationRoute(route) || '/',
    exact: true,
  });
}

/** Returns only the bounded local registry data required to verify an alarm. */
export async function getAthenaNativeAlarms() {
  if (!IS_NATIVE_APP) return { available: false, alarms: [] };
  return listNativeAthenaAlarms();
}

export async function cancelAthenaNativeAlarm(alarmId) {
  if (!IS_NATIVE_APP) return { ok: false, reason: 'not_native' };
  return cancelNativeAthenaAlarm(cleanIdentifier(alarmId, 120));
}

export async function snoozeAthenaNativeAlarm(alarmId, minutes = 5) {
  if (!IS_NATIVE_APP) return { ok: false, reason: 'not_native' };
  return snoozeNativeAthenaAlarm(cleanIdentifier(alarmId, 120), minutes);
}

export async function dismissAthenaNativeAlarm(alarmId) {
  if (!IS_NATIVE_APP) return { ok: false, reason: 'not_native' };
  return dismissNativeAthenaAlarm(cleanIdentifier(alarmId, 120));
}

// Provider-shaped public boundary for UI/diagnostics consumers. It contains
// no state and performs no boot-time reconciliation or automatic scheduling.
export const NativeAlarmProvider = Object.freeze({
  scheduleForReminder: scheduleAthenaAlarmForReminder,
  list: getAthenaNativeAlarms,
  cancel: cancelAthenaNativeAlarm,
  snooze: snoozeAthenaNativeAlarm,
  dismiss: dismissAthenaNativeAlarm,
});

export async function getAthenaNotificationsStatus(authToken) {
  if (IS_NATIVE_APP) {
    const [local, remote] = await Promise.all([
      getAthenaNotificationStatus(),
      authToken ? fetch(`${API_BASE}/push/status`, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null) : Promise.resolve(null),
    ]);
    return remote ? { ...local, ...remote } : local;
  }
  return getAthenaPushStatus(authToken);
}

export async function testAthenaNotifications(authToken) {
  // The existing Worker test uses the same device registry and is useful for
  // both providers. Native only reaches this path after its real FCM token has
  // been registered; it never falls back to Firebase Web Messaging.
  if (IS_NATIVE_APP) {
    if (!authToken) throw new Error('Sessão não encontrada');
    const registration = await getNativePushRegistration();
    if (!registration?.deviceId) {
      throw new Error('Ative as notificações neste aparelho antes de executar o teste');
    }
    const response = await fetch(`${API_BASE}/push/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ device_id: registration.deviceId }),
    });
    if (!response.ok) throw new Error(`Teste de push falhou (${response.status})`);
    return response.json();
  }
  return testAthenaPush(authToken);
}

/** Web foreground listener. Never call this in Capacitor. */
export async function observeWebAthenaNotifications(onReceived) {
  if (IS_NATIVE_APP) return noop;
  return observeAthenaForeground((payload) => {
    const notification = emitAthenaNotificationReceived(payload);
    if (notification) onReceived?.(notification);
  });
}

function nativeEventIdentity(payload) {
  const root = asRecord(payload);
  return cleanIdentifier(root.event_id || root.eventId, 160);
}

function createNativeEventDispatcher({ onReceived, onAction, onTokenRefresh } = {}) {
  const seenEventIds = new Set();
  const deliveryByNotificationId = new Map();

  return (payload, fallbackType = '') => {
    const eventId = nativeEventIdentity(payload);
    if (eventId) {
      if (seenEventIds.has(eventId)) return null;
      seenEventIds.add(eventId);
      // Native persistence is bounded to 60 entries. Keep this mirror small
      // too, while retaining enough history to de-dupe a drain after attach.
      if (seenEventIds.size > 80) seenEventIds.delete(seenEventIds.values().next().value);
    }

    const eventType = nativeEventName(payload, fallbackType);
    if (eventType === 'push_token_refresh') {
      const detail = emitAthenaNativeTokenRefresh(payload);
      if (detail) onTokenRefresh?.(detail);
      return detail;
    }

    const normalized = normalizeAthenaNotificationPayload(payload);
    if (!normalized) return null;

    if (normalized.id && normalized.deliveryId) {
      deliveryByNotificationId.set(normalized.id, normalized.deliveryId);
      if (deliveryByNotificationId.size > 80) deliveryByNotificationId.delete(deliveryByNotificationId.keys().next().value);
    }
    const cachedDeliveryId = normalized.id ? deliveryByNotificationId.get(normalized.id) : null;
    const notification = !normalized.deliveryId && cachedDeliveryId
      ? Object.freeze({ ...normalized, deliveryId: cachedDeliveryId })
      : normalized;

    if (eventType === 'notification_foreground') {
      const received = emitAthenaNotificationReceived(notification);
      if (received) onReceived?.(received);
      return received;
    }

    if (eventType === 'notification_received' || eventType === 'alarm_fired') {
      void reportAthenaNotificationEvent(notification, 'received');
      return notification;
    }

    if (eventType === 'notification_displayed') {
      void reportAthenaNotificationEvent(notification, 'displayed');
      return notification;
    }

    if (eventType === 'alarm_snoozed' || notification.action === 'snooze') {
      void reportAthenaNotificationEvent(notification, 'snoozed');
      return notification;
    }

    if (eventType === 'alarm_dismissed' || notification.action === 'dismiss') {
      void reportAthenaNotificationEvent(notification, 'dismissed');
      return notification;
    }

    if (eventType === 'notification_opened' || eventType === 'notification_action') {
      if (!notification.canOpen) return notification;
      const action = emitAthenaNotificationAction(notification);
      if (action) onAction?.(action);
      return action;
    }

    return notification;
  };
}

/**
 * Native listeners and the persisted event queue share one dispatcher. The
 * queue is drained before and after listener attachment so a WebView startup
 * race cannot lose `displayed`, `foreground`, `opened`, snooze or dismiss
 * events. This remains entirely local/native and never starts Firebase Web.
 */
export async function observeNativeAthenaNotifications(handlers = {}) {
  if (!IS_NATIVE_APP || !isNativeNotificationsPluginAvailable()) return noop;

  const dispatch = createNativeEventDispatcher(handlers);
  const drain = async () => {
    const pending = await getNativePendingNotificationEvents();
    pending.forEach((event) => dispatch(event));
  };

  await drain();
  const removers = await Promise.all([
    addNativeNotificationsListener('notificationEvent', (payload) => dispatch(payload)),
    addNativeNotificationsListener('notificationForeground', (payload) => dispatch(payload, 'notification_foreground')),
    addNativeNotificationsListener('notificationAction', (payload) => dispatch(payload, 'notification_opened')),
    addNativeNotificationsListener('tokenRefresh', (payload) => dispatch(payload, 'push_token_refresh')),
    addNativeNotificationsListener('alarmEvent', (payload) => dispatch(payload)),
  ]);
  await drain();

  return () => removers.forEach((remove) => {
    try { remove?.(); } catch { noop(); }
  });
}

/**
 * Clears a native push identity for a specific account after remote
 * unregistering. Logout intentionally does not fall back to the legacy global
 * `clearIdentity()` when an older APK lacks the owner-scoped method: that
 * could erase a newly logged-in account during a rapid account switch.
 */
export async function clearNativeAthenaNotificationIdentity(ownerId = '') {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return false;
  const safeOwnerId = cleanIdentifier(ownerId, 180);
  try {
    if (safeOwnerId) {
      const clearForOwner = plugin.clearIdentityForOwner || plugin.clearForOwner;
      if (typeof clearForOwner !== 'function') return false;
      const result = await clearForOwner.call(plugin, { ownerId: safeOwnerId });
      return result?.cleared === true || result?.ok === true || result?.ownerChanged === true;
    }
    if (typeof plugin.clearIdentity !== 'function') return false;
    await plugin.clearIdentity();
    return true;
  } catch {
    return false;
  }
}

/**
 * Disconnects the native device from the Worker before deleting the local
 * identity. This is intentionally invoked only by logout, after an explicit
 * prior activation; it never turns FCM on merely to find a token.
 */
export async function unregisterNativeAthenaNotifications(authToken, { ownerId = '' } = {}) {
  if (!IS_NATIVE_APP) return { ok: false, reason: 'not_native', registration: null };

  let registration = null;
  try {
    const status = await getAthenaNotificationStatus();
    if (status.pushEnabled === true) registration = await getNativePushRegistration();
  } catch {
    // The native identity is still cleared below; a logout must not leave an
    // account association in the WebView just because diagnostics failed.
  }

  let remoteOk = false;
  if (authToken && registration?.deviceId && registration?.token) {
    try {
      const response = await fetch(`${API_BASE}/push/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          device_id: registration.deviceId,
          token: registration.token,
        }),
      });
      remoteOk = response.ok;
    } catch {
      remoteOk = false;
    }
  }

  // The Worker call above always settles before owner-scoped identity clear. If offline,
  // AuthContext repeats the same identifiers in /auth/logout as a second
  // best-effort deactivation before it drops the session token.
  const localCleared = await clearNativeAthenaNotificationIdentity(ownerId);
  return {
    ok: remoteOk || !registration,
    remoteOk,
    localCleared,
    registration,
  };
}
